import { Add as AddIcon, Edit as EditIcon, Place as PlaceIcon } from '@mui/icons-material'
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import type { PickupLocation } from '@otw/models'
import type { Contact } from '@otw/models/leads/contacts'
import { getTerminalDisplayName } from '@otw/models/leads/terminals'
import { usePickupLocations } from 'hooks/inventory/usePickupLocations'
import { useTerminals } from 'hooks/terminals/useTerminals'
import React, { useMemo, useState } from 'react'
import AddLocationDialog from 'views/SuperAdmin/Management/PickupLocations/components/AddLocationDialog'
import {
  buildPickupLocationPrefillFromCompany,
  editedLocationIsShared,
} from '../util/companyLocationPrefill'
import { getPickupLocationLabel } from '../util/pickupLocationDisplay'
import { buildTerminalByPickupLocationId } from 'views/SuperAdmin/Management/TerminalManagement/util/terminalCompanyLinks'

export type AddressKind = 'business' | 'dropoff'

const ADDRESS_KIND_COPY: Record<
  AddressKind,
  { searchPlaceholder: string; addButtonLabel: string }
> = {
  business: {
    searchPlaceholder: 'Search business addresses...',
    addButtonLabel: 'Create new business address',
  },
  dropoff: {
    searchPlaceholder: 'Search dropoff addresses...',
    addButtonLabel: 'Create new dropoff address',
  },
}

export interface PickupLocationSelectorProps {
  value: string | null | undefined
  onChange: (locationId: string | null) => void
  label: string
  /** Controls search placeholder and add-button wording in the UI. */
  addressKind: AddressKind
  required?: boolean
  disabled?: boolean
  error?: string
  helperText?: string
  /** Prefills `company_name` when adding a new pickup location. */
  companyName?: string
  /** Contacts linked to the company — used to prefill and pick location contacts. */
  memberContacts?: Contact[]
  /** Allow saving with only city and/or state (no street address). */
  allowPartialAddress?: boolean
  /** When false, hides the inline create-new-address button (parent may render one shared button). */
  showCreateButton?: boolean
  /** When false, hides the edit button for the selected address. Defaults to true. */
  showEditButton?: boolean
  /**
   * Location ids selected by other address fields on the same form.
   * If this field uses one of them, editing copies the location instead of
   * rewriting the shared record.
   */
  otherLocationIds?: Array<string | null | undefined>
}

const PickupLocationSelector: React.FC<PickupLocationSelectorProps> = ({
  value,
  onChange,
  label,
  addressKind,
  required = false,
  disabled = false,
  error,
  helperText,
  companyName,
  memberContacts,
  allowPartialAddress = false,
  showCreateButton = true,
  showEditButton = true,
  otherLocationIds,
}) => {
  const copy = ADDRESS_KIND_COPY[addressKind]
  const { pickupLocations, isLoading, createPickupLocation, updatePickupLocation } =
    usePickupLocations()
  const { terminals } = useTerminals()
  const terminalByLocationId = useMemo(
    () => buildTerminalByPickupLocationId(terminals),
    [terminals],
  )
  const [locationDialogMode, setLocationDialogMode] = useState<'create' | 'edit' | null>(null)

  const newLocationPrefill = useMemo(
    () =>
      buildPickupLocationPrefillFromCompany(companyName ?? '', memberContacts ?? []) ??
      (companyName?.trim()
        ? ({ company_name: companyName.trim() } as PickupLocation)
        : null),
    [companyName, memberContacts],
  )

  const selectedLocation = useMemo(
    () => pickupLocations?.find((loc) => loc.id === value) ?? null,
    [pickupLocations, value],
  )

  return (
    <>
      <Autocomplete
        options={pickupLocations ?? []}
        value={selectedLocation}
        onChange={(_, newValue) => onChange(newValue?.id ?? null)}
        loading={isLoading}
        disabled={disabled}
        getOptionLabel={(location) => {
          const terminal = location.id
            ? terminalByLocationId.get(location.id)
            : undefined
          return getPickupLocationLabel(location, {
            terminalLabel: terminal
              ? getTerminalDisplayName(terminal, location)
              : undefined,
          })
        }}
        isOptionEqualToValue={(option, val) => option.id === val.id}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            required={required}
            error={!!error}
            helperText={error || helperText}
            placeholder={copy.searchPlaceholder}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  <InputAdornment position='start'>
                    <PlaceIcon fontSize='small' color='action' />
                  </InputAdornment>
                  {params.InputProps.startAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, location) => {
          const terminal = location.id
            ? terminalByLocationId.get(location.id)
            : undefined
          return (
            <Box component='li' {...props} key={location.id} sx={{ py: 1.5 }}>
              <Box sx={{ width: '100%' }}>
                <Stack direction='row' spacing={1} alignItems='center'>
                  {location.company_name || terminal ? (
                    <Typography variant='body2' fontWeight='bold'>
                      {terminal
                        ? getTerminalDisplayName(terminal, location)
                        : location.company_name}
                    </Typography>
                  ) : null}
                  {terminal ? (
                    <Chip
                      size='small'
                      label='Terminal'
                      color='info'
                      variant='outlined'
                      sx={{ height: 18, fontSize: '0.65rem' }}
                    />
                  ) : null}
                </Stack>
                {!terminal && location.contact_name ? (
                  <Typography variant='body2' fontWeight={location.company_name ? undefined : 'bold'}>
                    {location.contact_name}
                  </Typography>
                ) : null}
                <Typography variant='body2' color='text.secondary'>
                  {[location.address, location.city, location.state, location.zip]
                    .map((part) => part?.trim())
                    .filter(Boolean)
                    .join(', ') || 'No address on file'}
                </Typography>
                {location.contact_phone ? (
                  <Typography variant='caption' color='text.secondary' display='block'>
                    {location.contact_phone}
                  </Typography>
                ) : null}
              </Box>
            </Box>
          )
        }}
      />
      {showCreateButton || (showEditButton && selectedLocation) ? (
        <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap sx={{ mt: 1 }}>
          {showEditButton && selectedLocation ? (
            <Button
              variant='outlined'
              size='small'
              startIcon={<EditIcon />}
              onClick={() => setLocationDialogMode('edit')}
              disabled={disabled}
              sx={{ textTransform: 'none' }}
            >
              Edit selected address
            </Button>
          ) : null}
          {showCreateButton ? (
            <Button
              variant='outlined'
              size='small'
              startIcon={<AddIcon />}
              onClick={() => setLocationDialogMode('create')}
              disabled={disabled}
              sx={{ textTransform: 'none' }}
            >
              {copy.addButtonLabel}
            </Button>
          ) : null}
        </Stack>
      ) : null}

      {locationDialogMode ? (
        <AddLocationDialog
          open
          onClose={() => setLocationDialogMode(null)}
          location={locationDialogMode === 'edit' ? selectedLocation : newLocationPrefill}
          isRepairShop={false}
          allowPartialAddress={allowPartialAddress}
          memberContacts={memberContacts}
          requireContactInfo={
            locationDialogMode === 'create' && !allowPartialAddress && !memberContacts?.length
          }
          onCreate={async (data) => {
            const created = await createPickupLocation(
              data as Omit<PickupLocation, 'id' | 'created_at' | 'updated_at'>,
            )
            onChange(created.id ?? null)
            setLocationDialogMode(null)
          }}
          onUpdate={async (data) => {
            const location = data as PickupLocation
            if (editedLocationIsShared(value, otherLocationIds ?? [])) {
              const { id: _id, created_at: _createdAt, updated_at: _updatedAt, ...fields } =
                location
              const created = await createPickupLocation(fields)
              onChange(created.id ?? null)
            } else {
              await updatePickupLocation(location)
            }
            setLocationDialogMode(null)
          }}
        />
      ) : null}
    </>
  )
}

export default PickupLocationSelector
